import { ValidationStatuses } from '@sunwebgroup/uds-ui/fields/base-field'
import React from 'react'
import { DateFormat } from '@/scopes/common/domain/types'

import { isValidDate, parseDay, toUTCDay } from '@/scopes/common/domain/utils/dates'

// eslint-disable-next-line regexp/no-obscure-range
export const namePattern = /^[A-ZÀ-ÿ][A-ZÀ-ÿ' -]{1,49}$/i
export const datePattern = /^[1-9]\d{3}-\d{2}-\d{2}$/ // YYYY-MM-DD format
export const emailPattern = /^[\w.%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i
export const lengthValidationFieldBirthdate = 10 // YYYY-MM-DD format

export const isValidDateFormat = (val: string) => datePattern.test(val)

// TODO We should take a decision on when we produce the Date object
export function validateAgeBetweenDates(
  value: string | Date,
  min?: Date,
  max?: Date,
  dateFormat?: DateFormat | undefined,
) {
  const date = parseDate(value, dateFormat)

  if (!date) {
    return false
  }

  if (!isValidDate(date)) {
    return false
  }

  const dateToValidate = normalizeDateToValidate(date)
  const minDateToValidate = min ? normalizeDateToValidate(new Date(min)) : undefined
  const maxDateToValidate = max ? normalizeDateToValidate(new Date(max)) : undefined

  if (minDateToValidate && dateToValidate < minDateToValidate) {
    return false
  }

  if (maxDateToValidate && dateToValidate > maxDateToValidate) {
    return false
  }

  return true
}

function parseDate(date: string | Date, dateFormat: DateFormat = 'yyyy-MM-dd') {
  if (!(date instanceof Date)) {
    if (!date || date.length < lengthValidationFieldBirthdate) {
      return undefined
    }

    return parseDay(date, dateFormat)
  }

  return toUTCDay(date)
}

function normalizeDateToValidate(date: Date) {
  return toUTCDay(date).getTime()
}
/**
 * Custom hook to determine the validation status of a form field based on its value, error state,
 * interaction state, and other validation criteria.
 *
 * @param {object} params - The parameters for the hook.
 * @param {string} params.value - The current value of the field.
 * @param {object} [params.error] - An optional error object containing a message if the field is invalid.
 * @param {string} [params.error.message] - The error message associated with the field.
 * @param {boolean} params.isDirty - Indicates whether the field has been interacted with.
 * @param {number} params.fieldLengthValidation - The minimum length required for the field value to be considered valid.
 * @param {boolean} params.isSubmitting - Indicates whether the form is currently being submitted.
 * @param {boolean} [params.matchPattern] - Optional flag to indicate if the field value matches a specific pattern.
 *
 * @returns {ValidationStatuses} - The current validation status of the field. Possible values are:
 * - `'default'`: The field has not been interacted with or does not meet validation criteria.
 * - `'error'`: The field has an error after submission.
 * - `'success'`: The field meets all validation criteria.
 *
 * @remarks
 * This hook tracks the interaction state of the field (e.g., whether it has been blurred or submitted)
 * and updates the validation status accordingly. It also resets the submission state if the field becomes valid.
 * Possible changes of state: default -> success, default -> submitted -> error, default -> submitted -> error (onChange restart stateMachine).
 *
 * @example
 * const validationStatus = useSmartFieldStatus({
 *   value: inputValue,
 *   error: fieldError,
 *   isDirty: isFieldDirty,
 *   fieldLengthValidation: 5,
 *   isSubmitting: isFormSubmitting,
 *   matchPattern: true,
 * });
 */
export function useSmartFieldStatus({
  value,
  error,
  isDirty,
  fieldLengthValidation,
  isSubmitting,
  matchPattern = true,
}: {
  value: string
  error?: { message: string } | undefined
  isDirty: boolean
  fieldLengthValidation: number
  isSubmitting: boolean
  matchPattern?: boolean
}): ValidationStatuses {
  const [wasBlurred, setWasBlurred] = React.useState(false)
  const [wasSubmitted, setWasSubmitted] = React.useState(false)
  const [wasInvalid, setWasInvalid] = React.useState(false)
  const trimmedValue = value?.trim() ?? ''
  React.useEffect(() => {
    if (isDirty && !wasBlurred) {
      setWasBlurred(true)
    }
  }, [isDirty, wasBlurred])

  React.useEffect(() => {
    if (!wasSubmitted && isSubmitting) {
      setWasSubmitted(true)
    }
  }, [wasSubmitted, isSubmitting])

  React.useEffect(() => {
    if (error) {
      setWasInvalid(true)
    }
  }, [error])

  let result: ValidationStatuses = 'default'
  if (!trimmedValue && !wasSubmitted) {
    result = 'default'
  }
  else if (wasSubmitted && error) {
    result = 'error'
  }
  else if (wasBlurred && trimmedValue.length >= fieldLengthValidation && !error && matchPattern) {
    result = 'success'
  }
  else if (wasBlurred && trimmedValue.length >= fieldLengthValidation && matchPattern) {
    if (wasInvalid || wasBlurred) {
      result = 'success'
    }
  }
  return result
}
